import type { JSX } from 'react';
import { Link } from 'wouter';
import { useWindows } from '../hooks/queries';
import { clockTime, serviceDayLabel, windowRange } from '../lib/format';
import { chosenMornings, isSoonestChoice } from '../lib/stay';
import { resolvePromisedWindow } from '../lib/windows';
import { useBasket } from '../state/basket-provider';
import { useCheckoutDraft } from '../state/checkout';
import { useLocale } from '../state/locale';
import { useSession } from '../state/session';

/**
 * The mornings the guest already chose, restated with the way back to them
 * (PRD GST-002, GST-026).
 *
 * The delivery time is chosen away from this screen, which means everything
 * after it is being read against a decision made minutes ago and off screen. A
 * guest who cannot see that decision cannot check it, and a guest who cannot
 * change it without starting again will empty their basket to do so. So this
 * block sits above the shop list and the basket, and the link carries `?next=`
 * back to the screen it was opened from - the basket is untouched by the round
 * trip, because the picker writes nothing until the guest confirms.
 *
 * ONE ROW PER MORNING, under one label.
 *
 * A stay is several mornings on one order (D11: one picker for every order), and
 * this block used to render the anchor alone - "Arriving Wednesday, 07:00-07:30"
 * - as though a four-morning stay were a Wednesday. The other three were visible
 * nowhere until the checkout recap, so the guest's only reading of what they had
 * booked disagreed with what they were about to pay for. The label names the
 * relationship ("Order for") and every selected morning gets its own line
 * beneath it, one morning or five.
 *
 * Every line carries the SAME window, and that is the product rule rather than a
 * shortcut: one window is chosen for the whole stay, and a day that cannot be
 * served in it is greyed out of the picker rather than offered a different one
 * (D1, D2). So the range is resolved once, against the anchor, and restated per
 * day - a per-day lookup would imply a per-day choice that cannot be made.
 *
 * The range comes from the picker's own answer, already in the cache from the
 * screen before (`resolvePromisedWindow`). When that is not loaded the line says
 * the start time and calls it a start time: window length is the platform's to
 * configure (RTE-028), never a screen's to assume.
 *
 * no-eternal-spinner: rendered by the screen that owns its own query; this reads
 * a cache and degrades to the start time rather than to a spinner.
 *
 * It renders NOTHING before a morning is chosen, which in mode B is the whole
 * time the guest is browsing - but the `useWindows` read above still runs,
 * because hooks run before that return. That is worth keeping rather than
 * guarding: it fills the picker's own cache under the exact key the picker asks
 * for, so the screen the basket bar leads to opens on data instead of on a
 * spinner. One request, on the screen before the one that needs it.
 */
export function ChosenMorning({ backTo }: { backTo: string }): JSX.Element | null {
  const { locale, t } = useLocale();
  const { session } = useSession();
  const { basket } = useBasket();
  const { draft } = useCheckoutDraft();
  /** Same key the picker asks under, so this is a cache read on the way past. */
  const windows = useWindows(basket, null);

  const { serviceDay, windowStart } = draft;
  if (serviceDay === null || windowStart === null) return null;

  const timeZone = windows.data?.timeZone ?? session?.timeZone ?? 'Europe/Paris';
  const promised = resolvePromisedWindow(undefined, windows.data, serviceDay, windowStart);
  const window =
    promised === null
      ? t('checkout.windowFrom', { time: clockTime(windowStart, locale, timeZone) })
      : windowRange(promised, locale);

  /* The same set `stayForWire` builds the order from - see `chosenMornings`. */
  const days = chosenMornings(draft.selectedDays, serviceDay);
  const change = (
    <Link href={`/windows?next=${encodeURIComponent(backTo)}`} className="gw-textlink">
      {t('windows.change')}
    </Link>
  );

  /*
   * An order for TODAY is a different promise and reads as one (GST-042,
   * RTE-034). There is no morning to name - it is this one - so the block leads
   * with the arrival range and says which product it is underneath. Which day
   * is today is the server's answer, carried on the picker's response as
   * `kind`, never the browser's (TST-003).
   *
   * "Change" stays, and is the only way into the picker in a journey that has
   * no picker step: it opens scoped to today, so a guest can take a later
   * window this afternoon without the app growing a second control.
   */
  if (isSoonestChoice(windows.data, serviceDay, windowStart)) {
    /*
     * RTE-034: no time here, deliberately - and only for the ASAP row.
     *
     * This block is rendered on the menu and the basket, before there is a
     * basket to quote, and an order for NOW arrives when a courier says so for
     * a pickup that does not exist yet. The range is named once, on the review
     * screen, from the quote. A slot the guest named this afternoon is not this
     * case: it has a time because they chose one, and it falls through to the
     * ordinary block below.
     */
    return (
      <div className="gw-chosenday">
        <div className="gw-chosenday__days">
          <span className="gw-chosenday__text">{t('menu.arrivingToday')}</span>
        </div>
        {change}
      </div>
    );
  }

  return (
    <div className="gw-chosenday">
      <div className="gw-chosenday__days">
        <span className="gw-chosenday__label">{t('windows.orderFor')}</span>
        <ul className="gw-chosenday__list">
          {days.map((day) => (
            <li key={day} className="gw-chosenday__row">
              <span className="gw-chosenday__day">{serviceDayLabel(day, locale, timeZone)}</span>
              <span className="gw-chosenday__window">{window}</span>
            </li>
          ))}
        </ul>
      </div>
      {change}
    </div>
  );
}
